import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../layouts/main/main_layout";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { getFMPortalSurveys } from "../../services/fm_portal/fm_portal";
import { black } from "../../theme/colors";

export default function HourlyReport() {
  const navigate = useNavigate();
  const [date, setDate] = useState(() => {
    if (typeof window === "undefined") return "";
    return sessionStorage.getItem("fmPortalSelectedDate") || "";
  });
  const [selectedSurvey, setSelectedSurvey] = useState<any>(null);

  const { data: surveysResponse, isLoading } = useQuery(
    ["fmPortalSurveys", date],
    () => getFMPortalSurveys(date),
    {
      enabled: Boolean(date),
      staleTime: 0,
    }
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (date) {
      sessionStorage.setItem("fmPortalSelectedDate", date);
    } else {
      sessionStorage.removeItem("fmPortalSelectedDate");
    }
  }, [date]);

  const surveys = useMemo(() => {
    const payload = surveysResponse?.data?.data ?? surveysResponse?.data ?? [];
    return Array.isArray(payload) ? payload : payload?.list ?? [];
  }, [surveysResponse]);

  return (
    <MainLayout>
      <Box px={2} py={3} width="100%">
        <Card
          sx={{
            maxWidth: 520,
            width: "100%",
            px: 3,
            py: 3,
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
            borderRadius: 2,
            mx: "auto",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Typography variant="h6" color={black} mb={1.5} fontWeight={600}>
              Hourly Report
            </Typography>
            <Divider
              sx={{
                display: "flex",
                opacity: 0.5,
                borderBottom: "1px dotted #7C797C",
                mt: 1,
                mb: 3,
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Select Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              sx={{ mb: 3 }}
            />

            {date ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Projects for {date}
                </Typography>

                {isLoading ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                ) : surveys.length ? (
                  <Autocomplete
                    options={surveys}
                    value={selectedSurvey}
                    onChange={(_, value) => {
                      setSelectedSurvey(value);

                      if (!value) return;

                      const surveyId = value.choiceId ?? value.id ?? value.surveyId;
                      const surveyName = value.choiceName ?? value.name ?? value.title ?? "Survey";

                      navigate("/fm-portal/hourly-report/details", {
                        state: {
                          date,
                          choiceId: surveyId,
                          surveyName,
                        },
                      });
                    }}
                    getOptionLabel={(option: any) => {
                      const surveyId = option.choiceId ?? option.id ?? option.surveyId;
                      const surveyName = option.choiceName ?? option.name ?? option.title ?? "Survey";
                      return surveyId ? `${surveyName} (ID: ${surveyId})` : surveyName;
                    }}
                    isOptionEqualToValue={(option: any, value: any) =>
                      String(option.choiceId ?? option.id ?? option.surveyId ?? "") ===
                      String(value?.choiceId ?? value?.id ?? value?.surveyId ?? "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Select Project"
                        placeholder="Search project"
                      />
                    )}
                    sx={{ mb: 2 }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No projects found for the selected date.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a date to load the project list.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
}

